'use client';

import { useReportWebVitals } from 'next/web-vitals';

import ClientLogger from '~logger/client';
import { LoggerMessages, LoggerServices } from '~logger/constants';
import { routes } from '~shared/constants/routes';

import { sendMetricToAPI } from './sendMetricToAPI';

// Из исходников пакета Web Vitals, он нам целиком не нужен, только этот тип
interface Metric {
	/**
	 * The name of the metric (in acronym form).
	 */
	name: 'CLS' | 'FCP' | 'INP' | 'LCP' | 'TTFB';

	/**
	 * The current value of the metric.
	 */
	value: number;

	/**
	 * The rating as to whether the metric value is within the "good",
	 * "needs improvement", or "poor" thresholds of the metric.
	 */
	rating: 'good' | 'needs-improvement' | 'poor';

	/**
	 * The delta between the current value and the last-reported value.
	 * On the first report, `delta` and `value` will always be the same.
	 */
	delta: number;

	/**
	 * A unique ID representing this particular metric instance. This ID can
	 * be used by an analytics tool to dedupe multiple values sent for the same
	 * metric instance, or to group multiple deltas together and calculate a
	 * total. It can also be used to differentiate multiple different metric
	 * instances sent from the same page, which can happen if the page is
	 * restored from the back/forward cache (in that case new metrics object
	 * get created).
	 */
	id: string;

	/**
	 * Any performance entries relevant to the metric value calculation.
	 * The array may also be empty if the metric value was not based on any
	 * entries (e.g. a CLS value of 0 given no layout shifts).
	 */
	entries: PerformanceEntry[];

	/**
	 * The type of navigation.
	 *
	 * This will be the value returned by the Navigation Timing API (or
	 * `undefined` if the browser doesn't support that API), with the following
	 * exceptions:
	 * - 'back-forward-cache': for pages that are restored from the bfcache.
	 * - 'back_forward' is renamed to 'back-forward' for consistency.
	 * - 'prerender': for pages that were prerendered.
	 * - 'restore': for pages that were discarded by the browser and then
	 * restored by the user.
	 */
	navigationType:
		| 'navigate'
		| 'reload'
		| 'back-forward'
		| 'back-forward-cache'
		| 'prerender'
		| 'restore';
}

const logWebVitals = (metric: Metric) => {
	const tags = [
		`navigation:${metric.navigationType}`,
		`rating:${metric.rating}`,
	];

	const routeKey = findRouteKey(window.location.pathname, routes);
	if (routeKey) {
		tags.push(`route:${routeKey}`);
	}

	void sendMetricToAPI(
		{
			name: `web-vitals.${metric.name.toLowerCase()}`,
			value: metric.value,
			type: 'gauge',
			tags,
		},
		LoggerMessages.MetricWebVitalsFailed
	);
};

export function WebVitals() {
	useReportWebVitals(logWebVitals);

	return null;
}

// Beware, LLM slop
const findRouteKey = (
	path: string,
	routesObject: object,
	parentKey?: string
): string | null => {
	try {
		for (const key in routesObject) {
			const currentKey = parentKey ? `${parentKey}.${key}` : key;
			const value = routesObject[key as keyof typeof routesObject];

			if (typeof value === 'string' && value === path) {
				return currentKey;
			}

			if (typeof value === 'function') {
				const fn = value as (arg: string | number) => string;
				try {
					const basePattern = fn('([^/]+)');
					const regex = new RegExp(`^${basePattern}$`);
					if (regex.test(path)) {
						return currentKey;
					}
				} catch {
					continue;
				}
			}

			if (typeof value === 'object' && value !== null) {
				const result = findRouteKey(path, value, currentKey);
				if (result) {
					return result;
				}
			}
		}
		return null;
	} catch (error) {
		ClientLogger.error(
			LoggerServices.MetricsService,
			LoggerMessages.MetricWebVitalsFailed,
			error as Record<string, unknown>
		);
		return null;
	}
};
